import { NavLink } from 'react-router-dom';
import { HelpCircle, X, Building2, MapPin, Store, ChevronDown, Check, ChevronRight, Calendar, Loader2 } from 'lucide-react';
import { navigation, secondaryNavigation, assistantNavigation } from '../../config/navigation';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useBusinessUnit } from '../../hooks/useBusinessUnit';
import { useFiscalYear } from '../../hooks/useFiscalYear';
import type { BusinessUnitType } from '../../types/business-unit';
import { useState, useRef, useEffect, useCallback } from 'react';

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
  collapsed?: boolean; // Mode réduit quand le chat est ouvert
}

// Helpers pour les icônes et labels des types d'unités
const getBusinessUnitIcon = (type: BusinessUnitType) => {
  switch (type) {
    case 'company': return Building2;
    case 'branch': return MapPin;
    case 'pos': return Store;
    default: return Building2; // Fallback
  }
};

const getBusinessUnitTypeLabel = (type: BusinessUnitType) => {
  switch (type) {
    case 'company': return 'Entreprise';
    case 'branch': return 'Succursale';
    case 'pos': return 'Point de vente';
    default: return 'Unité'; // Fallback
  }
};

// Composant sélecteur d'exercice fiscal pour la sidebar (mode étendu)
function SidebarFiscalYearSelector() {
  const { fiscalYears, selectFiscalYear, loading } = useFiscalYear();
  const { currentFiscalYear } = useFiscalYearStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((fy: typeof fiscalYears[0]) => {
    if (fy.id === currentFiscalYear?.id) {
      setIsOpen(false);
      return;
    }
    setIsRefreshing(true);
    selectFiscalYear(fy);
    setIsOpen(false);
    // Laisser le temps aux hooks de recharger puis masquer l'indicateur
    const timer = setTimeout(() => setIsRefreshing(false), 2500);
    return () => clearTimeout(timer);
  }, [currentFiscalYear?.id, selectFiscalYear]);

  const statusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Ouvert';
      case 'closed': return 'Clôturé';
      case 'audited': return 'Audité';
      default: return status;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'closed': return 'bg-gray-400';
      case 'audited': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const showProgress = isRefreshing || loading;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton déclencheur + barre de progression discrète */}
      <div className="relative overflow-hidden rounded-md">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md bg-primary-foreground/10 dark:bg-muted hover:bg-primary-foreground/20 dark:hover:bg-muted/80 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            {showProgress ? (
              <Loader2 className="h-4 w-4 text-primary-foreground/70 dark:text-muted-foreground flex-shrink-0 animate-spin" />
            ) : (
              <Calendar className="h-4 w-4 text-primary-foreground/70 dark:text-muted-foreground flex-shrink-0" />
            )}
            <div className="text-left min-w-0">
              <div className="text-xs text-primary-foreground/60 dark:text-muted-foreground">Exercice actif</div>
              <div className="text-sm font-medium text-primary-foreground dark:text-foreground truncate">
                {currentFiscalYear?.code || 'Aucun exercice'}
              </div>
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 text-primary-foreground/70 dark:text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Progress bar discrète en bas du bouton */}
        {showProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/20 overflow-hidden">
            <div className="h-full bg-primary animate-progress-bar" />
          </div>
        )}
      </div>

      {/* Dropdown s'ouvre vers le BAS pour ne pas passer sous le header */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-64 overflow-hidden">
          {/* Progress bar en haut du dropdown */}
          {showProgress && (
            <div className="h-0.5 bg-primary/20 overflow-hidden">
              <div className="h-full bg-primary animate-progress-bar" />
            </div>
          )}

          <div className="px-3 py-2 border-b border-border bg-muted/50">
            <div className="text-xs font-medium text-muted-foreground">
              {showProgress ? 'Actualisation des données...' : 'Sélectionner l\'exercice'}
            </div>
          </div>

          <div className="overflow-y-auto max-h-52">
            {fiscalYears.length === 0 ? (
              <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                Aucun exercice disponible
              </div>
            ) : (
              fiscalYears.map((fy) => (
                <button
                  key={fy.id}
                  onClick={() => handleSelect(fy)}
                  disabled={showProgress}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 ${
                    currentFiscalYear?.id === fy.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(fy.status)}`} />
                  <div className="flex-1 text-left min-w-0">
                    <div className="truncate font-medium">{fy.code}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(fy.startDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      {' — '}
                      {new Date(fy.endDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      {' · '}
                      {statusLabel(fy.status)}
                    </div>
                  </div>
                  {currentFiscalYear?.id === fy.id && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant sélecteur d'exercice fiscal compact (sidebar collapsed)
function SidebarFiscalYearSelectorCollapsed() {
  const { fiscalYears, selectFiscalYear, loading } = useFiscalYear();
  const { currentFiscalYear } = useFiscalYearStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((fy: typeof fiscalYears[0]) => {
    if (fy.id === currentFiscalYear?.id) {
      setIsOpen(false);
      return;
    }
    setIsRefreshing(true);
    selectFiscalYear(fy);
    setIsOpen(false);
    const timer = setTimeout(() => setIsRefreshing(false), 2500);
    return () => clearTimeout(timer);
  }, [currentFiscalYear?.id, selectFiscalYear]);

  const statusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'closed': return 'bg-gray-400';
      case 'audited': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const showProgress = isRefreshing || loading;

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative overflow-hidden rounded-md">
        <button
          onClick={() => setIsOpen(!isOpen)}
          title={`Exercice: ${currentFiscalYear?.code || 'Aucun'}`}
          className="w-full flex items-center justify-center p-2 rounded-md bg-primary-foreground/10 dark:bg-muted hover:bg-primary-foreground/20 dark:hover:bg-muted/80 transition-colors"
        >
          {showProgress ? (
            <Loader2 className="h-5 w-5 text-primary-foreground/70 dark:text-muted-foreground animate-spin" />
          ) : (
            <Calendar className="h-5 w-5 text-primary-foreground/70 dark:text-muted-foreground" />
          )}
        </button>
        {showProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/20 overflow-hidden">
            <div className="h-full bg-primary animate-progress-bar" />
          </div>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-0 left-full ml-2 w-56 bg-card border border-border rounded-lg shadow-lg z-[9999] max-h-64 overflow-hidden">
          {showProgress && (
            <div className="h-0.5 bg-primary/20 overflow-hidden">
              <div className="h-full bg-primary animate-progress-bar" />
            </div>
          )}
          <div className="px-3 py-2 border-b border-border bg-muted/50">
            <div className="text-xs font-medium text-muted-foreground">
              {showProgress ? 'Actualisation...' : 'Exercice fiscal'}
            </div>
          </div>
          <div className="overflow-y-auto max-h-52">
            {fiscalYears.map((fy) => (
              <button
                key={fy.id}
                onClick={() => handleSelect(fy)}
                disabled={showProgress}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors disabled:opacity-50 ${
                  currentFiscalYear?.id === fy.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                }`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(fy.status)}`} />
                <span className="truncate">{fy.code}</span>
                {currentFiscalYear?.id === fy.id && (
                  <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Composant sélecteur de périmètre pour la sidebar (mode étendu)
function SidebarBusinessUnitSelector() {
  const { hierarchy, selectedUnit, selectUnit, includeChildren, setIncludeChildren } = useBusinessUnit();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SelectedIcon = selectedUnit ? getBusinessUnitIcon(selectedUnit.type) : Building2;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton déclencheur */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md bg-primary-foreground/10 dark:bg-muted hover:bg-primary-foreground/20 dark:hover:bg-muted/80 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <SelectedIcon className="h-4 w-4 text-primary-foreground/70 dark:text-muted-foreground flex-shrink-0" />
          <div className="text-left min-w-0">
            <div className="text-xs text-primary-foreground/60 dark:text-muted-foreground">Périmètre</div>
            <div className="text-sm font-medium text-primary-foreground dark:text-foreground truncate">
              {selectedUnit?.name || 'Entreprise générale'}
            </div>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-primary-foreground/70 dark:text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
          {/* Option Entreprise */}
          {hierarchy && (
            <button
              onClick={() => {
                selectUnit(hierarchy.company);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                selectedUnit?.id === hierarchy.company.id ? 'bg-primary/10 text-primary' : 'text-foreground'
              }`}
            >
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{hierarchy.company.name}</span>
              {selectedUnit?.id === hierarchy.company.id && (
                <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
              )}
            </button>
          )}

          {/* Succursales */}
          {hierarchy?.branches && hierarchy.branches.length > 0 && (
            <>
              <div className="px-3 py-1 text-xs text-muted-foreground font-medium bg-muted/50">
                Succursales
              </div>
              {hierarchy.branches.map((branch) => {
                // Filtrer les POS de cette succursale depuis hierarchy.pointsOfSale
                const branchPOS = hierarchy.pointsOfSale?.filter(pos => pos.branchId === branch.id) || [];
                
                return (
                <div key={branch.id}>
                  <button
                    onClick={() => {
                      selectUnit(branch);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                      selectedUnit?.id === branch.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                    }`}
                  >
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{branch.name}</span>
                    {selectedUnit?.id === branch.id && (
                      <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
                    )}
                  </button>
                  
                  {/* Points de vente de cette succursale */}
                  {branchPOS.length > 0 && (
                    <div className="pl-6">
                      {branchPOS.map((pos) => (
                        <button
                          key={pos.id}
                          onClick={() => {
                            selectUnit(pos);
                            setIsOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors ${
                            selectedUnit?.id === pos.id ? 'bg-primary/10 text-primary' : 'text-foreground/80'
                          }`}
                        >
                          <Store className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate text-xs">{pos.name}</span>
                          {selectedUnit?.id === pos.id && (
                            <Check className="h-3.5 w-3.5 ml-auto text-primary flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );})}
            </>
          )}

          {/* Option inclure sous-unités */}
          {selectedUnit && selectedUnit.type !== 'pos' && (
            <div className="border-t border-border px-3 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-xs text-muted-foreground">
                  Inclure les sous-unités
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Composant sélecteur de périmètre compact pour la sidebar en mode collapsed
function SidebarBusinessUnitSelectorCollapsed() {
  const { hierarchy, selectedUnit, selectUnit, includeChildren, setIncludeChildren } = useBusinessUnit();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const SelectedIcon = selectedUnit ? getBusinessUnitIcon(selectedUnit.type) : Building2;
  const typeLabel = selectedUnit ? getBusinessUnitTypeLabel(selectedUnit.type) : 'Entreprise';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bouton compact - icône uniquement */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={`Périmètre: ${selectedUnit?.name || 'Entreprise générale'} (${typeLabel})`}
        className="w-full flex items-center justify-center p-2 rounded-md bg-primary-foreground/10 dark:bg-muted hover:bg-primary-foreground/20 dark:hover:bg-muted/80 transition-colors"
      >
        <SelectedIcon className="h-5 w-5 text-primary-foreground/70 dark:text-muted-foreground" />
      </button>

      {/* Dropdown positionné à droite */}
      {isOpen && (
        <div className="absolute bottom-0 left-full ml-2 w-64 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {/* Header */}
          <div className="px-3 py-2 border-b border-border bg-muted/50">
            <div className="text-xs font-medium text-muted-foreground">Sélectionner le périmètre</div>
          </div>
          
          {/* Option Entreprise */}
          {hierarchy && (
            <button
              onClick={() => {
                selectUnit(hierarchy.company);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                selectedUnit?.id === hierarchy.company.id ? 'bg-primary/10 text-primary' : 'text-foreground'
              }`}
            >
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{hierarchy.company.name}</span>
              {selectedUnit?.id === hierarchy.company.id && (
                <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
              )}
            </button>
          )}

          {/* Succursales */}
          {hierarchy?.branches && hierarchy.branches.length > 0 && (
            <>
              <div className="px-3 py-1 text-xs text-muted-foreground font-medium bg-muted/50">
                Succursales
              </div>
              {hierarchy.branches.map((branch) => {
                const branchPOS = hierarchy.pointsOfSale?.filter(pos => pos.branchId === branch.id) || [];
                
                return (
                  <div key={branch.id}>
                    <button
                      onClick={() => {
                        selectUnit(branch);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                        selectedUnit?.id === branch.id ? 'bg-primary/10 text-primary' : 'text-foreground'
                      }`}
                    >
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{branch.name}</span>
                      {selectedUnit?.id === branch.id && (
                        <Check className="h-4 w-4 ml-auto text-primary flex-shrink-0" />
                      )}
                    </button>
                    
                    {/* Points de vente */}
                    {branchPOS.length > 0 && (
                      <div className="pl-6">
                        {branchPOS.map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => {
                              selectUnit(pos);
                              setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors ${
                              selectedUnit?.id === pos.id ? 'bg-primary/10 text-primary' : 'text-foreground/80'
                            }`}
                          >
                            <Store className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate text-xs">{pos.name}</span>
                            {selectedUnit?.id === pos.id && (
                              <Check className="h-3.5 w-3.5 ml-auto text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Option inclure sous-unités */}
          {selectedUnit && selectedUnit.type !== 'pos' && (
            <div className="border-t border-border px-3 py-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-xs text-muted-foreground">
                  Inclure les sous-unités
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ isOpen, onClose, collapsed = false }: SidebarProps) {
  const { currentFiscalYear } = useFiscalYearStore();

  // Mode réduit : uniquement les icônes
  const NavItem = ({ item }: { item: any }) => (
    <NavLink
      key={item.path}
      to={item.path}
      onClick={onClose}
      title={collapsed ? item.name : undefined}
      className={({ isActive }) => `
        flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3 py-2 text-sm font-medium rounded-md
        transition-colors duration-200
        ${isActive 
          ? 'bg-primary-foreground text-primary dark:bg-primary dark:text-primary-foreground' 
          : 'text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-foreground dark:hover:bg-muted dark:hover:text-foreground'}
      `}
    >
      <div className="flex items-center">
        <item.icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'}`} />
        {!collapsed && item.name}
      </div>
      {!collapsed && item.badge && (
        <span className={`${item.badge.color} text-white text-xs px-2 py-0.5 rounded-full`}>
          {item.badge.text}
        </span>
      )}
    </NavLink>
  );

  const SectionTitle = ({ title }: { title: string }) => (
    !collapsed ? (
      <div className="px-3 py-2 text-xs font-semibold text-primary-foreground/50 dark:text-muted-foreground uppercase tracking-wider">
        {title}
      </div>
    ) : (
      <div className="border-t border-primary-foreground/20 dark:border-border my-2" />
    )
  );

  return (
    <aside className={`
      fixed inset-y-0 left-0 z-20 bg-primary dark:bg-card transform 
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      transition-all duration-300 ease-in-out lg:translate-x-0 lg:static
      pt-16 flex flex-col h-screen border-r border-border
      ${collapsed ? 'w-16' : 'w-64'}
    `}>
      {!collapsed && (
        <div className="flex items-center justify-between px-4 py-2 lg:hidden">
          <span className="text-primary-foreground dark:text-foreground font-medium">Menu</span>
          {onClose && (
            <button 
              onClick={onClose} 
              className="text-primary-foreground/80 hover:text-primary-foreground dark:text-muted-foreground dark:hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Exercice fiscal - Sélecteur interactif */}
      {!collapsed && (
        <div className="mx-3 mb-2">
          <SidebarFiscalYearSelector />
        </div>
      )}
      {collapsed && (
        <div className="mx-2 mb-2">
          <SidebarFiscalYearSelectorCollapsed />
        </div>
      )}

      <nav className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-3 py-2">
          {/* Comptabilité - Navigation principale */}
          <SectionTitle title="Comptabilité" />
          <div className="space-y-1 mb-4">
            {navigation.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          {/* Administration */}
          <SectionTitle title="Administration" />
          <div className="space-y-1 mb-4">
            {secondaryNavigation.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          {/* Assistant IA */}
          <div className="space-y-1">
            {assistantNavigation.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </div>
      </nav>

      {/* Help Section */}
      <div className={`${collapsed ? 'p-2' : 'p-4'} border-t border-primary-foreground/20 dark:border-border space-y-3`}>
        {/* Sélecteur de périmètre - version adaptée au mode */}
        {collapsed ? <SidebarBusinessUnitSelectorCollapsed /> : <SidebarBusinessUnitSelector />}
        
        <NavLink
          to="/settings"
          onClick={onClose}
          title={collapsed ? "Aide & Support" : undefined}
          className={`flex items-center ${collapsed ? 'justify-center' : ''} px-3 py-2 text-sm font-medium rounded-md text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground transition-colors duration-200`}
        >
          <HelpCircle className={`h-5 w-5 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && 'Aide & Support'}
        </NavLink>
      </div>
    </aside>
  );
}
